import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, RefreshCw, Gift, HelpCircle, AlertCircle, AlertTriangle, CreditCard, Truck, PackageCheck, Package, Check, Store } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import ProgressBar from '@/components/ProgressBar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { createReturnRequest, getPublicSettings } from '@/lib/api';
import { cn } from '@/lib/utils';

// Helper function to check if item is bridal/wedding dress (respects overrides from admin settings)
const isBridalItem = (item, overrides = []) => {
  // Check overrides first — admin can force a product to "standard" or "bridal" by product_id
  if (overrides && overrides.length > 0) {
    const itemProductId = String(item.product_id || '');
    const override = overrides.find(o => String(o.product_id) === itemProductId);
    if (override) {
      return override.force_category === 'bridal';
    }
  }
  
  const title = (item.title || '').toLowerCase();
  const productType = (item.product_type || '').toLowerCase();
  const tags = (item.tags || '').toLowerCase();
  const bridalKeywords = ['abito da sposa', 'collezione sposa', 'abiti sposa', 'bride', 'bridal', 'wedding dress'];
  
  return bridalKeywords.some(keyword => 
    title.includes(keyword) || productType.includes(keyword) || tags.includes(keyword)
  );
};

// Cost constants
const EXCHANGE_FEE_NORMAL = 20.00;
const EXCHANGE_FEE_BRIDAL = 100.00;

const Summary = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [exchangeItems, setExchangeItems] = useState([]);
  const [requestType, setRequestType] = useState(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitGuardRef = useRef(false);
  const [eventDate, setEventDate] = useState('');
  const [hasUnavailableItems, setHasUnavailableItems] = useState(false);
  const [publicSettings, setPublicSettings] = useState({
    voucher_bonus_percent: 10,
    shipping_cost_base: 35,
  });
  
  // Confirmation checkboxes
  const [confirmCondition, setConfirmCondition] = useState(false);
  const [confirmPackaging, setConfirmPackaging] = useState(false);
  const [confirmLegalAct, setConfirmLegalAct] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const missingEmail = order && !order.email;
  
  // Delivery method for exchange and voucher — initialized dynamically from public settings
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const [confirmStoreDelivery, setConfirmStoreDelivery] = useState(false);

  useEffect(() => {
    const storedOrder = sessionStorage.getItem('currentOrder');
    const storedItems = sessionStorage.getItem('selectedItems');
    const storedType = sessionStorage.getItem('requestType');
    const storedReason = sessionStorage.getItem('reason');
    const storedExchangeItems = sessionStorage.getItem('exchangeItems');
    
    if (storedOrder && storedItems && storedType) {
      setOrder(JSON.parse(storedOrder));
      setSelectedItems(JSON.parse(storedItems));
      setRequestType(storedType);
      if (storedReason) setReason(storedReason);
      if (storedExchangeItems) setExchangeItems(JSON.parse(storedExchangeItems));
      
      const storedEventDate = sessionStorage.getItem('eventDate');
      const storedHasUnavailable = sessionStorage.getItem('hasUnavailableItems');
      if (storedEventDate) setEventDate(storedEventDate);
      if (storedHasUnavailable === 'true') setHasUnavailableItems(true);
    } else {
      navigate('/');
    }

    // Load public settings for dynamic voucher bonus
    getPublicSettings().then(data => {
      setPublicSettings(data);
      // Set default delivery method from admin config
      const storedType = sessionStorage.getItem('requestType');
      const configuredMethod = storedType === 'exchange' 
        ? (data.shipping_method_exchange || 'pickup')
        : storedType === 'voucher'
        ? (data.shipping_method_voucher || 'pickup')
        : 'pickup';
      setDeliveryMethod(prev => prev || configuredMethod);
    }).catch(() => {
      setDeliveryMethod(prev => prev || 'pickup');
    });
  }, [navigate]);

  const handleSubmit = async () => {
    // Guard: prevent double submission (ref is synchronous, unlike setState)
    if (submitGuardRef.current) return;
    submitGuardRef.current = true;

    // Check if all confirmations are checked
    if (!confirmCondition || !confirmPackaging || !confirmLegalAct) {
      toast.error('Devi accettare tutte le dichiarazioni per procedere');
      submitGuardRef.current = false;
      return;
    }
    
    // Validate contact email if order has no email
    const finalEmail = order.email || contactEmail.trim();
    if (!finalEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(finalEmail)) {
      toast.error('Inserisci un indirizzo email valido per ricevere le comunicazioni');
      submitGuardRef.current = false;
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format return_items
      const formattedReturnItems = selectedItems.map(item => ({
        product_id: String(item.id),
        variant_id: String(item.variant_id),
        title: item.title,
        variant_title: item.variant_title,
        quantity: item.quantity || 1,
        price: String(item.price),
        image_url: item.image_url || null
      }));

      // Format exchange_items if it's an exchange
      let formattedExchangeItems = null;
      if (requestType === 'exchange' && exchangeItems.length > 0) {
        formattedExchangeItems = exchangeItems.map(item => ({
          product_id: String(item.product_id),
          variant_id: String(item.variant_id),
          title: item.title,
          variant_title: item.variant_title,
          quantity: 1,
          price: String(item.price),
          image_url: item.image_url || null
        }));
      }

      const result = await createReturnRequest({
        order_id: String(order.id),
        order_number: order.order_number,
        customer_email: finalEmail,
        customer_name: order.customer_name,
        customer_phone: order.shipping_address?.phone || null,
        shipping_address: order.shipping_address || null,
        request_type: requestType,
        return_items: formattedReturnItems,
        exchange_items: formattedExchangeItems,
        reason: reason,
        delivery_method: (requestType === 'exchange' || requestType === 'voucher') ? deliveryMethod : null,
        event_date: eventDate || null,
        has_unavailable_items: hasUnavailableItems,
        delivered_at: order.delivered_at || null,
        fulfilled_at: order.fulfilled_at || null,
        paid_with_gift_card: order.paid_with_gift_card || false,
        cost_waiver: order.cost_waiver || false
      });
      
      // Clear session storage
      sessionStorage.removeItem('selectedItems');
      sessionStorage.removeItem('requestType');
      sessionStorage.removeItem('reason');
      sessionStorage.removeItem('exchangeItems');
      sessionStorage.removeItem('eventDate');
      sessionStorage.removeItem('hasUnavailableItems');
      
      navigate(`/confirmation/${result.id}`);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Errore durante l\'invio della richiesta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (requestType === 'exchange') {
      navigate(`/order/${orderNumber}/exchange`);
    } else {
      navigate(`/order/${orderNumber}/type`);
    }
  };

  const calculateReturnTotal = () => {
    return selectedItems.reduce((sum, item) => sum + parseFloat(item.price), 0);
  };

  const calculateExchangeTotal = () => {
    return exchangeItems.reduce((sum, item) => sum + parseFloat(item.price), 0);
  };

  if (!order) {
    return null;
  }

  // Gift card order detection
  const paidWithGiftCard = order.paid_with_gift_card || false;
  const costWaiver = order.cost_waiver || false;

  const returnTotal = calculateReturnTotal();
  const exchangeTotal = calculateExchangeTotal();
  const priceDifference = exchangeTotal - returnTotal;
  
  // Check if any item is bridal
  const hasBridalItem = selectedItems.some(item => isBridalItem(item, publicSettings.product_category_overrides));
  const EXCHANGE_FEE_BASE = hasBridalItem ? EXCHANGE_FEE_BRIDAL : (publicSettings.exchange_fee ?? EXCHANGE_FEE_NORMAL);
  
  // Exchange fee depends on delivery method: pickup = €20 (or €100 bridal), store = reservation fee, cost_waiver = €0
  const STORE_RESERVATION_FEE = publicSettings.store_reservation_fee ?? 10;
  const STORE_RESERVATION_DAYS = publicSettings.store_reservation_days ?? 14;
  const STORE_RESERVATION_LABEL = (publicSettings.store_reservation_label || 'Riserva prodotto in negozio');
  const STORE_RESERVATION_DESC = (publicSettings.store_reservation_description || 'Il prodotto sarà riservato per {days} giorni presso il nostro negozio.').replace('{days}', STORE_RESERVATION_DAYS);
  const STORE_DELIVERY_LABEL = publicSettings.store_reservation_delivery_label || 'Porta il pacco e ritira il nuovo articolo in negozio';
  const EXCHANGE_FEE = costWaiver ? 0 : (deliveryMethod === 'store' ? STORE_RESERVATION_FEE : EXCHANGE_FEE_BASE);
  
  // Calculate net amount: negative price_difference + fee
  const netAmount = priceDifference + EXCHANGE_FEE;
  const hasRefund = netAmount < 0;
  const refundAmount = hasRefund ? Math.abs(netAmount) : 0;
  const totalToPay = hasRefund ? 0 : netAmount;
  const bonusPercent = publicSettings.voucher_bonus_percent || 0;
  const hasBonus = bonusPercent > 0;

  // Calcola costo gestione reso in base ai prezzi degli articoli
  const calculateReturnCostFromItems = (items, settings) => {
    const threshold = settings.shipping_price_threshold || 249;
    const firstBelow = settings.shipping_cost_first_below || 35;
    const firstAbove = settings.shipping_cost_first_above || 50;
    const addBelow = settings.shipping_cost_additional_below || 15;
    const addAbove = settings.shipping_cost_additional_above || 25;
    const sorted = [...items].sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    let cost = 0;
    sorted.forEach((item, i) => {
      const price = parseFloat(item.price);
      if (i === 0) {
        cost += price >= threshold ? firstAbove : firstBelow;
      } else {
        cost += price >= threshold ? addAbove : addBelow;
      }
    });
    return cost;
  };

  const returnCostForGiftCard = costWaiver ? 0 : calculateReturnCostFromItems(selectedItems, publicSettings);

  // Calcolo valore voucher dinamico
  let voucherValue;
  if (costWaiver) {
    voucherValue = returnTotal * (1 + bonusPercent / 100);
  } else if (paidWithGiftCard) {
    voucherValue = Math.max(returnTotal - returnCostForGiftCard, 0);
  } else {
    voucherValue = returnTotal * (1 + bonusPercent / 100);
  }

  // Get request type display info
  const getRequestTypeInfo = () => {
    switch (requestType) {
      case 'exchange':
        return {
          label: 'Richiesta di Cambio',
          icon: RefreshCw,
          bgColor: 'border-blue-200 bg-blue-50 text-blue-800',
          itemsLabel: 'Articoli da Cambiare'
        };
      case 'voucher':
        return {
          label: 'Richiesta Voucher',
          icon: Gift,
          bgColor: 'border-emerald-200 bg-emerald-50 text-emerald-800',
          itemsLabel: 'Articoli per Voucher'
        };
      case 'other':
        return {
          label: 'Altra Richiesta',
          icon: HelpCircle,
          bgColor: 'border-zinc-200 bg-zinc-50 text-zinc-800',
          itemsLabel: 'Articoli'
        };
      default:
        return {
          label: 'Richiesta',
          icon: HelpCircle,
          bgColor: 'border-zinc-200 bg-zinc-50 text-zinc-800',
          itemsLabel: 'Articoli'
        };
    }
  };

  const typeInfo = getRequestTypeInfo();
  const TypeIcon = typeInfo.icon;

  return (
    <div className="page-container bg-white">
      <Header showBack backTo={`/order/${orderNumber}/type`} />
      <ProgressBar currentStep={4} totalSteps={5} />
      
      <main className="flex-1 py-8 px-4 md:py-12 md:px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Passaggio 4 di 5
            </p>
            <h1 className="font-heading text-2xl md:text-4xl font-normal tracking-tight mb-2">
              Riepilogo Richiesta
            </h1>
            <p className="text-muted-foreground font-light mb-8">
              Verifica i dettagli prima di confermare
            </p>
          </motion.div>

          {/* Request Type Badge */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className={cn(
              "inline-flex items-center gap-3 px-4 py-2 border",
              typeInfo.bgColor
            )}>
              <TypeIcon className="w-5 h-5" />
              <span className="font-bold uppercase tracking-wider text-sm">
                {typeInfo.label}
              </span>
            </div>
          </motion.div>

          {/* Order Info */}
          <motion.div 
            className="luxury-card p-4 md:p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3">
              Ordine
            </p>
            <p className="font-medium">{order.order_number}</p>
            <p className="text-sm text-muted-foreground">{order.customer_name}{order.email ? ` • ${order.email}` : ''}</p>
            
            {missingEmail && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">
                  Inserisci la tua email per ricevere le comunicazioni
                </p>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="nome@email.com"
                  className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  data-testid="missing-email-input"
                />
                <p className="text-xs text-amber-600 mt-1.5">
                  Riceverai qui la conferma e tutte le comunicazioni sulla tua richiesta.
                </p>
              </div>
            )}
          </motion.div>

          {/* Products to Return */}
          <motion.div 
            className="luxury-card p-4 md:p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
              {typeInfo.itemsLabel} ({selectedItems.length})
            </p>
            <div className="space-y-4">
              {selectedItems.map((item, index) => (
                <div key={item.unique_id || index} className="flex items-center gap-4">
                  <div className="w-14 h-18 md:w-16 md:h-20 bg-secondary overflow-hidden flex-shrink-0">
                    {item.image_url && (
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm md:text-base truncate">{item.title}</p>
                    <p className="text-xs md:text-sm text-muted-foreground">{item.variant_title}</p>
                  </div>
                  <p className="font-medium text-sm md:text-base">€{parseFloat(item.price).toFixed(2)}</p>
                </div>
              ))}
              <div className="border-t border-border pt-4 flex justify-between">
                <span className="font-medium">Valore articoli</span>
                <span className="font-bold text-lg">€{returnTotal.toFixed(2)}</span>
              </div>
            </div>
          </motion.div>

          {/* Exchange Items (if exchange) */}
          {requestType === 'exchange' && exchangeItems.length > 0 && (
            <motion.div 
              className="luxury-card p-4 md:p-6 mb-6 border-blue-200 bg-blue-50/30"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
                Nuovi Articoli Scelti ({exchangeItems.length})
              </p>
              <div className="space-y-4">
                {exchangeItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-14 h-18 md:w-16 md:h-20 bg-secondary overflow-hidden flex-shrink-0">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm md:text-base truncate">{item.title}</p>
                      <p className="text-xs md:text-sm text-muted-foreground">{item.variant_title}</p>
                    </div>
                    <p className="font-medium text-sm md:text-base">€{parseFloat(item.price).toFixed(2)}</p>
                  </div>
                ))}
                <div className="border-t border-blue-200 pt-4 flex justify-between">
                  <span className="font-medium">Valore nuovi articoli</span>
                  <span className="font-bold text-lg">€{exchangeTotal.toFixed(2)}</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Out-of-stock warning in summary */}
          {requestType === 'exchange' && hasUnavailableItems && (
            <motion.div 
              className="luxury-card p-4 md:p-6 mb-6 border-orange-300 bg-orange-50/50"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.27 }}
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-900">Richiesta soggetta ad approvazione</p>
                  <p className="text-xs text-orange-700 mt-1">
                    Hai selezionato un prodotto attualmente non disponibile in magazzino. 
                    La tua richiesta verrà valutata dal nostro staff e potrebbe essere rifiutata 
                    nel caso in cui non sia possibile rispettare la data del tuo evento.
                  </p>
                  {eventDate && (
                    <p className="text-sm text-orange-800 mt-2 font-medium">
                      Data evento: {new Date(eventDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                  <p className="text-xs text-orange-600 mt-2">
                    In caso di approvazione, riceverai una nota dal brand con la data prevista di produzione. 
                    In caso di rifiuto, potrai riavviare la procedura per scegliere un altro prodotto.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Delivery Method Choice — dynamic from admin settings */}
          {(requestType === 'exchange' || requestType === 'voucher') && (() => {
            const storeEnabled = requestType === 'exchange' 
              ? publicSettings.store_delivery_exchange_enabled 
              : publicSettings.store_delivery_voucher_enabled;
            const maxDays = publicSettings.store_delivery_max_days || 15;
            const notifDays = publicSettings.store_delivery_notification_days || 4;
            const address = publicSettings.return_shipping_address || {};
            const addressLine = [address.name, address.street, `${address.zip || ''} ${address.city || ''}`.trim(), address.province].filter(Boolean).join(' — ');
            
            // Get configured standard shipping method
            const configuredMethod = requestType === 'exchange'
              ? (publicSettings.shipping_method_exchange || 'pickup')
              : (publicSettings.shipping_method_voucher || 'pickup');
            
            // Get labels from admin settings
            const labels = publicSettings.delivery_method_labels || {};
            const standardLabel = labels[`${configuredMethod}_label`] || (configuredMethod === 'pickup' ? 'Ritiro a domicilio' : configuredMethod === 'label' ? 'Spedizione autonoma' : 'Spedizione guidata');
            const standardDesc = labels[`${configuredMethod}_description`] || '';
            
            // If only one option (no store delivery), show just info
            if (!storeEnabled) {
              if (requestType !== 'exchange') return null;
              return (
                <motion.div 
                  className="luxury-card p-4 md:p-6 mb-6 border-gray-200"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.28 }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <Truck className="w-5 h-5 text-gray-700" />
                    <p className="text-xs font-bold tracking-[0.15em] uppercase text-gray-700">
                      Metodo di Consegna
                    </p>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div>
                      <p className="font-medium text-sm">{standardLabel}</p>
                      {standardDesc && <p className="text-xs text-muted-foreground">{standardDesc}</p>}
                    </div>
                    <span className="font-bold">€{EXCHANGE_FEE_BASE.toFixed(2)}</span>
                  </div>
                </motion.div>
              );
            }
            
            // Multiple options: standard method + store
            return (
              <motion.div 
                className="luxury-card p-4 md:p-6 mb-6 border-gray-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.28 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Truck className="w-5 h-5 text-gray-700" />
                  <p className="text-xs font-bold tracking-[0.15em] uppercase text-gray-700">
                    Metodo di Consegna
                  </p>
                </div>
                
                <div className="grid gap-3">
                  {/* Standard shipping method (pickup/label/free) */}
                  <div
                    onClick={() => { setDeliveryMethod(configuredMethod); setConfirmStoreDelivery(false); }}
                    className={cn(
                      "p-4 rounded-lg border-2 cursor-pointer transition-all",
                      deliveryMethod !== 'store'
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                    data-testid="delivery-method-standard"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                          deliveryMethod !== 'store' ? "border-primary" : "border-gray-300"
                        )}>
                          {deliveryMethod !== 'store' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{standardLabel}</p>
                          {standardDesc && <p className="text-xs text-muted-foreground">{standardDesc}</p>}
                        </div>
                      </div>
                      {requestType === 'exchange' && (
                        <span className="font-bold">€{EXCHANGE_FEE_BASE.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Consegna in negozio — €0 */}
                  <div
                    onClick={() => setDeliveryMethod('store')}
                    className={cn(
                      "p-4 rounded-lg border-2 cursor-pointer transition-all",
                      deliveryMethod === 'store'
                        ? "border-teal-500 bg-teal-50 ring-2 ring-teal-200"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                    data-testid="delivery-method-store"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                          deliveryMethod === 'store' ? "border-teal-500" : "border-gray-300"
                        )}>
                          {deliveryMethod === 'store' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-teal-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">Consegna in negozio</p>
                          <p className="text-xs text-muted-foreground">{STORE_DELIVERY_LABEL}</p>
                        </div>
                      </div>
                      {requestType === 'exchange' && (
                        <span className={cn("font-bold", costWaiver ? "text-emerald-600" : "text-amber-600")}>
                          {costWaiver ? '€0.00' : `€${STORE_RESERVATION_FEE.toFixed(2)}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Store delivery warning + mandatory checkbox */}
                {deliveryMethod === 'store' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 overflow-hidden"
                  >
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-900">Importante — Leggi attentamente</p>
                          <ul className="text-xs text-amber-800 mt-2 space-y-2 list-disc pl-4">
                            <li>
                              <strong>Non recarti in negozio</strong> finché non ricevi un'email o un messaggio di conferma dal nostro servizio clienti. 
                              Senza questa comunicazione, lo staff del negozio non sarà a conoscenza della tua richiesta.
                            </li>
                            <li>
                              Hai fino a <strong>{maxDays} giorni</strong> dal ricevimento del pacco originale per consegnare il reso in negozio.
                            </li>
                            <li>
                              In casi eccezionali, se la nostra comunicazione arriva in ritardo, avrai comunque <strong>{notifDays} giorni</strong> dalla ricezione del messaggio.
                            </li>
                          </ul>
                        </div>
                      </div>
                      {addressLine && (
                        <div className="flex items-center gap-2 bg-white rounded px-3 py-2 border border-amber-200">
                          <Store className="w-4 h-4 text-amber-700 flex-shrink-0" />
                          <p className="text-xs text-amber-800"><strong>Indirizzo:</strong> {addressLine}</p>
                        </div>
                      )}
                      <label className="flex items-start gap-3 cursor-pointer bg-white rounded-lg p-3 border border-amber-200 hover:border-amber-400 transition-colors">
                        <Checkbox
                          checked={confirmStoreDelivery}
                          onCheckedChange={setConfirmStoreDelivery}
                          className="mt-0.5"
                          data-testid="confirm-store-delivery"
                        />
                        <span className="text-xs text-amber-900 font-medium">
                          Ho capito, attenderò la comunicazione del servizio clienti prima di recarmi in negozio.
                        </span>
                      </label>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })()}

          {/* Exchange Payment/Refund Summary */}
          {requestType === 'exchange' && (
            <motion.div 
              className={cn(
                "luxury-card p-4 md:p-6 mb-6",
                hasRefund 
                  ? "border-emerald-300 bg-emerald-50" 
                  : "border-amber-300 bg-amber-50"
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className={cn(
                  "w-5 h-5",
                  hasRefund ? "text-emerald-700" : "text-amber-700"
                )} />
                <p className={cn(
                  "text-xs font-bold tracking-[0.15em] uppercase",
                  hasRefund ? "text-emerald-700" : "text-amber-700"
                )}>
                  {hasRefund ? 'Riepilogo Credito' : 'Riepilogo Pagamento'}
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>
                    {deliveryMethod === 'store' 
                      ? `${STORE_RESERVATION_LABEL} (${STORE_RESERVATION_DAYS} giorni)`
                      : 'Costi di gestione cambio'}
                  </span>
                  <span className="font-medium">€{EXCHANGE_FEE.toFixed(2)}</span>
                </div>
                {deliveryMethod === 'store' && (
                  <p className="text-xs text-amber-600">
                    {STORE_RESERVATION_DESC}
                  </p>
                )}
                
                {priceDifference !== 0 && (
                  <div className="flex justify-between text-sm">
                    <span>
                      {priceDifference > 0 
                        ? 'Differenza prezzo (da pagare)' 
                        : 'Differenza prezzo (a tuo favore)'}
                    </span>
                    <span className={cn(
                      "font-medium",
                      priceDifference < 0 ? "text-emerald-600" : ""
                    )}>
                      {priceDifference > 0 ? '+' : ''}€{priceDifference.toFixed(2)}
                    </span>
                  </div>
                )}
                
                <div className={cn(
                  "border-t pt-3 flex justify-between items-center",
                  hasRefund ? "border-emerald-300" : "border-amber-300"
                )}>
                  {hasRefund ? (
                    <>
                      <span className="font-bold text-emerald-900">
                        {paidWithGiftCard ? 'Credito (voucher)' : 'Credito a tuo favore'}
                      </span>
                      <span className="font-bold text-2xl text-emerald-700">&euro;{refundAmount.toFixed(2)}</span>
                    </>
                  ) : (
                    <>
                      <span className="font-bold text-amber-900">Totale da pagare</span>
                      <span className="font-bold text-2xl text-amber-900">€{totalToPay.toFixed(2)}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Cost Breakdown */}
              {publicSettings.show_exchange_cost_explanation !== false && (
              <div className={cn(
                "mt-4 pt-4 border-t",
                hasRefund ? "border-emerald-200" : "border-amber-200"
              )}>
                <p className={cn(
                  "text-xs font-medium mb-2",
                  hasRefund ? "text-emerald-800" : "text-amber-800"
                )}>
                  {publicSettings.cost_explanations?.exchange_title || 'Perché il cambio ha questo costo?'}
                </p>
                <ul className={cn(
                  "text-xs space-y-1",
                  hasRefund ? "text-emerald-700" : "text-amber-700"
                )}>
                  {(publicSettings.cost_explanations?.exchange_items || 'Spedizione di andata - circa 13€ compreso di assicurazione|Spedizione di ritiro del prodotto - circa 13€ per richiesta di ritiro a domicilio').split('|').map((item, i) => (
                    <li key={`cost-${item.slice(0,20)}-${i}`}>• {item}</li>
                  ))}
                </ul>
                {hasBridalItem && (
                  <p className={cn(
                    "text-xs mt-2 font-medium",
                    hasRefund ? "text-emerald-800" : "text-amber-800"
                  )}>
                    {(publicSettings.cost_explanations?.bridal_warning || 'Gli abiti della collezione sposa hanno un costo fisso di €{bridal_cost} per via dei tessuti pregiati che richiedono trattamenti specializzati.').replace('{bridal_cost}', (publicSettings.bridal_return_cost || 100).toFixed(0))}
                  </p>
                )}
                {paidWithGiftCard && hasRefund && (
                  <p className="text-xs mt-2 text-amber-700 bg-amber-50 p-2 border border-amber-200 rounded">
                    Ordine pagato con gift card: il credito a tuo favore verrà emesso come voucher, non come rimborso monetario.
                  </p>
                )}
              </div>
              )}
            </motion.div>
          )}

          {/* Voucher Value (if voucher request type) */}
          {requestType === 'voucher' && (
            <motion.div 
              className="luxury-card p-4 md:p-6 mb-6 border-emerald-200 bg-emerald-50"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {paidWithGiftCard ? (
                /* Gift card order: show deduction */
                <div>
                  <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3">
                    Valore Voucher
                  </p>
                  <div className="space-y-2 mb-3">
                    <div className="flex justify-between text-sm">
                      <span>Valore articoli</span>
                      <span>&euro;{returnTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Costi di gestione reso</span>
                      <span>-&euro;{returnCostForGiftCard.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="border-t border-emerald-300 pt-3 flex items-center justify-between">
                    <span className="font-bold text-emerald-900">Voucher finale</span>
                    <span className="font-bold text-2xl text-emerald-700">&euro;{voucherValue.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-amber-700 mt-3 bg-amber-50 p-2 border border-amber-200">
                    Ordine pagato con gift card/credito: il rimborso avviene esclusivamente tramite voucher, al netto dei costi di gestione.
                  </p>
                </div>
              ) : (
                /* Normal order */
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-1">
                      Valore Voucher
                    </p>
                    <p className="text-sm text-emerald-700">
                      {hasBonus
                        ? `Include il bonus del ${bonusPercent}% sul valore degli articoli`
                        : `Pari al valore degli articoli selezionati`
                      }
                    </p>
                  </div>
                  <span className="font-bold text-2xl text-emerald-700">
                    &euro;{voucherValue.toFixed(2)}
                  </span>
                </div>
              )}
            </motion.div>
          )}

          {/* Shipping Address for VOUCHER and OTHER — shown only when customer ships (label/free) */}
          {(requestType === 'voucher' || requestType === 'other') && deliveryMethod !== 'store' && deliveryMethod !== 'pickup' && (() => {
            const returnAddress = publicSettings.return_shipping_address || {};
            if (!returnAddress.street) return null;
            return (
              <motion.div 
                className="luxury-card p-4 md:p-6 mb-6 border-blue-200 bg-blue-50"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Package className="w-5 h-5 text-blue-700" />
                  <p className="text-xs font-bold tracking-[0.15em] uppercase text-blue-700">
                    Indirizzo per la Spedizione
                  </p>
                </div>
                <div className="bg-white p-4 border border-blue-200">
                  <p className="font-medium">{returnAddress.name || 'DEA FEA'}</p>
                  <p className="text-sm text-muted-foreground">{returnAddress.street}</p>
                  <p className="text-sm text-muted-foreground">{returnAddress.zip} {returnAddress.city} ({returnAddress.province})</p>
                  <p className="text-sm text-muted-foreground">{returnAddress.country || 'Italia'}</p>
                </div>
                <p className="text-xs text-blue-700 mt-4">
                  {publicSettings?.email_texts?.recap_instructions_text || 'Spedisci il pacco a questo indirizzo. Ti consigliamo di usare un servizio tracciabile.'}
                </p>
              </motion.div>
            );
          })()}

          {/* Process Info for VOUCHER */}
          {/* Come funziona — Voucher (dynamic from admin settings) */}
          {requestType === 'voucher' && (() => {
            const method = deliveryMethod === 'store' ? 'pickup' : (publicSettings.shipping_method_voucher || 'pickup');
            const allSteps = publicSettings.come_funziona_steps || {};
            const steps = (allSteps[`voucher_${method}`] || []).filter(s => s.title);
            if (!steps.length) return null;
            return (
              <motion.div 
                className="luxury-card p-4 md:p-6 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
                  Come funziona
                </p>
                <div className="space-y-4">
                  {steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{step.title}</p>
                        {step.desc && <p className="text-xs text-muted-foreground">{step.desc}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })()}

          {/* Reason (only for "other" type) */}
          {requestType === 'other' && reason && (
            <motion.div 
              className="luxury-card p-4 md:p-6 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                La tua richiesta
              </p>
              <p className="text-sm">{reason}</p>
            </motion.div>
          )}

          {/* Come funziona il cambio — dynamic from admin settings */}
          {requestType === 'exchange' && (() => {
            const method = deliveryMethod === 'store' ? 'pickup' : (publicSettings.shipping_method_exchange || 'pickup');
            const allSteps = publicSettings.come_funziona_steps || {};
            const steps = (allSteps[`exchange_${method}`] || []).filter(s => s.title);
            if (!steps.length) return null;
            return (
              <motion.div 
                className="luxury-card p-4 md:p-6 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
              >
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
                  Come funziona il cambio
                </p>
                <div className="space-y-4">
                  {steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{step.title}</p>
                        {step.desc && <p className="text-xs text-muted-foreground">{step.desc}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })()}

          {/* Return Method Info with 14 days note */}
          <motion.div 
            className="luxury-card p-4 md:p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
              Modalità Restituzione
            </p>
            {(() => {
              const labels = publicSettings.delivery_method_labels || {};
              const method = deliveryMethod === 'store' ? null : (
                requestType === 'exchange'
                  ? (publicSettings.shipping_method_exchange || 'pickup')
                  : (publicSettings.shipping_method_voucher || 'pickup')
              );
              const methodLabel = method ? (labels[`${method}_label`] || 'Ritiro a Domicilio') : 'Consegna in negozio';
              const methodDesc = method ? (labels[`${method}_description`] || '') : 'Porta il pacco direttamente presso la nostra sede';
              return (
                <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3 mb-4">
                  <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">{methodLabel}</p>
                    {methodDesc && <p className="text-sm text-blue-700">{methodDesc}</p>}
                  </div>
                </div>
              );
            })()}
            <div className="bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                <strong>Nota:</strong> L'approvazione della richiesta avverrà dopo la verifica del rispetto della finestra di 14 giorni dalla consegna e del controllo delle condizioni del prodotto.
              </p>
            </div>
          </motion.div>

          {/* Confirmations */}
          <motion.div
            className="space-y-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.42 }}
          >
            <label className="flex items-start gap-3 cursor-pointer p-4 border border-border hover:border-primary/50 transition-colors">
              <Checkbox
                checked={confirmCondition}
                onCheckedChange={(checked) => {
                  setConfirmCondition(checked);
                  setConfirmPackaging(checked);
                }}
                className="mt-0.5"
                data-testid="confirm-condition"
              />
              <span className="text-sm">
                Confermo che il prodotto è nelle <strong>condizioni originali</strong>, non è stato utilizzato 
                (se non per prova), con etichette e accessori intatti, e che lo imballerò in modo <strong>sicuro e adeguato</strong> per la spedizione.
              </span>
            </label>
            
            <label className="flex items-start gap-3 cursor-pointer p-4 border border-border hover:border-primary/50 transition-colors">
              <Checkbox
                checked={confirmLegalAct}
                onCheckedChange={setConfirmLegalAct}
                className="mt-0.5"
                data-testid="confirm-legal-act"
              />
              <span className="text-sm">
                Dichiaro che quanto sopra indicato corrisponde a verità, <strong>accetto che i costi di gestione sopra indicati siano applicati</strong>, 
                e sono consapevole che la presente dichiarazione costituisce <strong>atto certificato e firmato</strong>, come già accettato in fase di ordine.
              </span>
            </label>
          </motion.div>

          {/* Important Notice - Only for exchange without refund */}
          {requestType === 'exchange' && !hasRefund && (
            <motion.div 
              className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.44 }}
            >
              <AlertCircle className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-600">
                <p className="font-medium mb-1">Nota importante</p>
                <p>Confermando la richiesta, accetti i termini del cambio. Il pagamento è necessario per procedere con la richiesta.</p>
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            className="flex items-center justify-between gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <Button 
              onClick={goBack}
              variant="outline"
              className="rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Modifica
            </Button>
            
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting || !confirmCondition || !confirmPackaging || !confirmLegalAct || (deliveryMethod === 'store' && !confirmStoreDelivery && (requestType === 'exchange' || requestType === 'voucher') && (requestType === 'exchange' ? publicSettings.store_delivery_exchange_enabled : publicSettings.store_delivery_voucher_enabled))}
              className="luxury-btn-primary inline-flex items-center gap-2"
              data-testid="confirm-submit-btn"
            >
              {isSubmitting 
                ? 'Elaborazione...' 
                : requestType === 'exchange' 
                  ? hasRefund
                    ? 'Conferma Cambio'
                    : `Paga €${totalToPay.toFixed(2)}`
                  : 'Conferma'
              }
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Summary;
